import { FC, useEffect, useState } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Select } from 'antd'
import { DashboardContainer } from '@forwardflow/components/Dashboard'
import Conversations from '@forwardflow/components/Dashboard/Conversations'
import { Button } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { useContactDetails } from '@forwardflow/hooks/customHooks/conversation/useContactDetails'
import { LeadList } from '@forwardflow/components/Dashboard/LeadList'
import { getStorageKey } from '@forwardflow/helpers/utils'
import { useActiveCampaigns } from '@forwardflow/hooks/customHooks/campaign/getActiveCampaigns'

const ConversationsPage: FC = () => {
   const { id } = useParams()
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const [isMenuOpen, setIsMenuOpen] = useState(false)

   // Get values from search params instead of URL params
   const campaign = searchParams.get('filter')
   const filter = searchParams.get('stages')

   const { data: activeCampaigns } = useActiveCampaigns({
      page: 1,
      limit: 10,
      status: 'active,paused',
   })

   const contactFilterOptions = [
      {
         name: 'All Stages',
         value: 'all-stages',
      },
      {
         name: 'Appt. set',
         value: 'ff-appointment-set',
      },
      {
         name: 'Appt. missed',
         value: 'ff-appointment-missed',
      },
   ]

   const getInitialCampaignId = () => {
      console.log('Getting initial campaign ID')
      if (!campaign || campaign === 'all-campaign') {
         console.log('No specific campaign selected')
         setSelectedCampaignName(null)
         return null
      }
      console.log('Campaign found:', campaign)

      const decodedCampaign = decodeURIComponent(campaign)
      console.log('Decoded campaign:', decodedCampaign)

      // if (decodedCampaign === 'Demo Source') {
      //    console.log('Setting to Demo Source')
      //    setSelectedCampaignName(decodedCampaign)
      //    return '4494a616f35e141a27f9b2bb41b15204'
      // }

      if (activeCampaigns) {
         const foundByCampaignName = activeCampaigns.find(
            (source) => source?.campaignName === decodedCampaign
         )

         const foundById = activeCampaigns.find(
            (source) => source?.id === decodedCampaign
         )

         const foundCampaign = foundByCampaignName || foundById

         if (foundCampaign) {
            setSelectedCampaignName(foundCampaign.campaignName)
            return foundCampaign.id
         }
      }
      setSelectedCampaignName(decodedCampaign)
      return decodedCampaign
   }

   useEffect(() => {
      if (activeCampaigns && selectedCampaignId === null) {
         const initialId = getInitialCampaignId()
         console.log('Initial campaign ID set to:', initialId)
         setSelectedCampaignId(initialId)
      }
   }, [activeCampaigns, campaign])

   const getInitialStageFilter = () => {
      if (filter === 'apt-set') return 'ff-appointment-set'
      if (filter === 'apt-missed') return 'ff-appointment-missed'
      return 'all-stages'
   }

   const [selectedStageFilter, setSelectedStageFilter] = useState<string>(
      getInitialStageFilter()
   )

   const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
      null
   )
   const [selectedCampaignName, setSelectedCampaignName] = useState<
      string | null
   >(null)

   const handleStageFilterSelect = (stage: string) => {
      setSelectedStageFilter(stage)
      let newSearchFilter = 'all-stages'
      if (stage === 'ff-appointment-set') {
         newSearchFilter = 'apt-set'
      } else if (stage === 'ff-appointment-missed') {
         newSearchFilter = 'apt-missed'
      }

      const isDemo = window.location.pathname.includes('demo-conversations-old')
      const basePath = isDemo ? '/demo-conversations-old' : '/conversations'

      const storageKey = getStorageKey(stage, selectedCampaignName)
      const storedLeadId = localStorage.getItem(storageKey)

      // Build search params
      const params = new URLSearchParams()
      if (newSearchFilter !== 'all-stages') {
         params.set('stages', newSearchFilter)
      }
      if (
         selectedCampaignName &&
         selectedCampaignName !== (isDemo ? 'Demo Source' : null)
      ) {
         params.set('filter', selectedCampaignName)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      if (storedLeadId) {
         navigate(`${basePath}/${storedLeadId}${urlSuffix}`)
      } else {
         navigate(`${basePath}${urlSuffix}`)
         setSelectedContactLeadId(null)
      }
   }

   useEffect(() => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')
      const isDemoCampaign = campaign === 'Demo Source'

      if (isDemo && !isDemoCampaign) {
         const basePath = '/demo-conversations-old'
         const params = new URLSearchParams()

         if (filter && filter !== 'all-stages') {
            params.set('stages', filter)
         }
         params.set('filter', 'Demo Source')

         const queryString = params.toString()
         const urlSuffix = queryString ? `?${queryString}` : ''

         if (id) {
            navigate(`${basePath}/${id}${urlSuffix}`, { replace: true })
         } else {
            navigate(`${basePath}${urlSuffix}`, { replace: true })
         }
      }
   }, [window.location.pathname, campaign, filter, id, navigate])

   useEffect(() => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')

      if (isDemo) {
         const demoSourceCampaign = activeCampaigns?.find(
            (source) => source?.campaignName === 'Demo Source'
         )

         const demoSourceId = demoSourceCampaign?.id ?? 'Demo Source'

         // const demoSourceId = '4494a616f35e141a27f9b2bb41b15204'

         if (
            selectedCampaignId !== demoSourceId ||
            selectedCampaignName !== 'Demo Source'
         ) {
            setSelectedCampaignId(demoSourceId)
            setSelectedCampaignName('Demo Source')
         }
      }
   }, [
      window.location.pathname,
      activeCampaigns,
      selectedCampaignId,
      selectedCampaignName,
   ])

   const [selectedContactLeadId, setSelectedContactLeadId] = useState<
      number | null
   >(id ? Number(id) : null)

   const {
      data: selectedContactDetails,
      isLoading,
      error,
   } = useContactDetails(id ? Number(id) : null)

   const handleLeadSelect = (leadId: number | null) => {
      setSelectedContactLeadId(leadId)

      if (leadId === null) {
         const basePath = window.location.pathname.includes(
            'demo-conversations-old'
         )
            ? '/demo-conversations-old'
            : '/conversations'

         let updatedSearchFilter = 'all-stages'
         if (selectedStageFilter === 'ff-appointment-set') {
            updatedSearchFilter = 'apt-set'
         } else if (selectedStageFilter === 'ff-appointment-missed') {
            updatedSearchFilter = 'apt-missed'
         }

         // Build search params
         const params = new URLSearchParams()
         if (updatedSearchFilter !== 'all-stages') {
            params.set('stages', updatedSearchFilter)
         }
         if (selectedCampaignName) {
            params.set('filter', selectedCampaignName)
         }

         const queryString = params.toString()
         const urlSuffix = queryString ? `?${queryString}` : ''

         navigate(`${basePath}${urlSuffix}`)
      }
   }

   const getLeadDetailsForConversation = () => {
      if (selectedContactLeadId === null) {
         return null
      }

      const isDemo = window.location.pathname.includes('demo-conversations-old')
      if (
         isDemo &&
         selectedContactDetails?.sourceInfo?.name !== 'Demo Source'
      ) {
         return undefined
      }

      if (error) {
         return undefined
      }
      return selectedContactDetails
   }

   const onSelectCampaign = (value: string, campaignName?: string | null) => {
      const isDemo = window.location.pathname.includes('demo-conversations-old')

      if (isDemo) {
         return
      }

      setSelectedCampaignId(value)

      const finalCampaignName =
         campaignName !== undefined
            ? campaignName
            : (() => {
                 if (value === null) {
                    return null
                 } else {
                    const selectedCampaign = activeCampaigns?.find(
                       (source) => source?.id === value
                    )
                    return selectedCampaign?.campaignName ?? value
                 }
              })()

      setSelectedCampaignName(finalCampaignName)

      let currentFilter = 'all-stages'
      if (selectedStageFilter === 'ff-appointment-set') {
         currentFilter = 'apt-set'
      } else if (selectedStageFilter === 'ff-appointment-missed') {
         currentFilter = 'apt-missed'
      }

      const storageKey = getStorageKey(
         selectedStageFilter,
         selectedCampaignName
      )
      const storedLeadId = localStorage.getItem(storageKey)

      // Build search params
      const params = new URLSearchParams()
      if (currentFilter !== 'all-stages') {
         params.set('stages', currentFilter)
      }
      if (finalCampaignName) {
         params.set('filter', finalCampaignName)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      if (storedLeadId) {
         navigate(`/conversations/${storedLeadId}${urlSuffix}`)
      } else {
         navigate(`/conversations${urlSuffix}`)
      }
   }

   return (
      <>
         <style>{`html { overflow: hidden; }`}</style>
         <DashboardContainer>
            <div className="bg-white/5 min-h-[52px] rounded-lg flex items-center mb-4">
               <Link to="/dashboard">
                  <Button className="!w-[36px] h-[36px] border border-transparent bg-transparent !rounded-lg !mt-0 ml-2 mr-4">
                     <Icon
                        name="arrow-right"
                        className="rotate-180 fill-white"
                        width={20}
                        height={20}
                     />
                  </Button>
               </Link>
               <span className="text-[#717171]">
                  <Link to="/dashboard"> Home /</Link>
                  <span className="text-white"> Conversations</span>
               </span>
               <div className="flex flex-wrap items-center gap-2 pr-2 ml-auto">
                  {/* Show hamburger for mobile/tablet */}
                  <div className="relative lg:hidden">
                     <Button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="!border-white/20 !bg-transparent !w-[38px] !h-[38px] !p-2 !mt-0 !rounded-lg"
                     >
                        <Icon name="Options" className="scale-[1] fill-white" />
                     </Button>

                     {/* Dropdown menu for mobile */}
                     {isMenuOpen && (
                        <div className="absolute left-0 z-50 bg-[#303137] rounded-lg shadow-lg px-4 pb-2 mt-2">
                           {contactFilterOptions?.map((option) => (
                              <Button
                                 key={option.value}
                                 onClick={() => {
                                    handleStageFilterSelect(option.value)
                                    setIsMenuOpen(false)
                                 }}
                                 className={`!bg-transparent text-white/70 w-full border-[#828282] !text-[14px] whitespace-nowrap text-left px-4 !h-auto !py-2 hover:bg-[#404147] ${
                                    selectedStageFilter === option.value
                                       ? 'text-white bg-[#404147]'
                                       : ''
                                 }`}
                              >
                                 {option.name}
                              </Button>
                           ))}
                        </div>
                     )}
                  </div>
                  <div className="hidden lg:flex lg:items-center lg:gap-2">
                     {contactFilterOptions?.map((option) => {
                        const isSelected = selectedStageFilter === option.value
                        return (
                           <Button
                              key={option.value}
                              onClick={() =>
                                 handleStageFilterSelect(option.value)
                              }
                              className={` text-white/70 group !w-auto hover:text-white hover:!bg-primary/20 !text-[14px] !font-medium whitespace-nowrap !h-[36px] !mt-0 px-[30px] !rounded-lg flex items-center ${
                                 isSelected
                                    ? 'text-white !bg-primary/20  !border-none'
                                    : '!bg-transparent !border-white/20'
                              }`}
                           >
                              {option.name}
                           </Button>
                        )
                     })}
                  </div>
                  <Select
                     className="min-w-[158px] dashboard-select !h-9 rounded"
                     placeholder={
                        window.location.pathname.includes(
                           'demo-conversations-old'
                        )
                           ? 'Demo Source'
                           : 'All Campaigns'
                     }
                     value={selectedCampaignId}
                     onChange={(value) => {
                        let campaignName: string | null = null

                        if (value === null) {
                           campaignName = null
                        } else {
                           const selectedCampaign = activeCampaigns?.find(
                              (source) => source?.id === value
                           )
                           campaignName = selectedCampaign?.campaignName ?? null
                        }

                        onSelectCampaign(value, campaignName)
                     }}
                     options={[
                        {
                           label: window.location.pathname.includes(
                              'demo-conversations-old'
                           )
                              ? 'Demo Source'
                              : 'All Campaigns',
                           value: window.location.pathname.includes(
                              'demo-conversations-old'
                           )
                              ? activeCampaigns?.filter(
                                   (source) =>
                                      source?.campaignName === 'Demo Source'
                                )[0]?.id ?? 'Demo Source'
                              : null,
                        },
                        ...(activeCampaigns
                           ?.filter((source) => {
                              if (
                                 window.location.pathname.includes(
                                    'demo-conversations-old'
                                 )
                              ) {
                                 return source?.campaignName !== 'Demo Source'
                              }
                              return true
                           })
                           ?.map((source) => ({
                              label: source?.campaignName,
                              value: source?.id,
                           })) || []),
                     ]}
                  />
               </div>
            </div>
            <div className="flex flex-col w-full gap-5 lg:flex-row max-h-[calc(100%-80px)]">
               <LeadList
                  onSelectLead={handleLeadSelect}
                  selectedStageFilter={selectedStageFilter}
                  selectedCampaignId={selectedCampaignId}
                  selectedCampaignName={selectedCampaignName}
               />
               <div className="flex-1 flex-grow">
                  <Conversations
                     selectedLeadDetails={getLeadDetailsForConversation()}
                     isLoading={isLoading}
                  />
               </div>
            </div>
         </DashboardContainer>
      </>
   )
}

export default ConversationsPage
